import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useGatewayUpdateCustomerFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.form-validation';
import { GatewayUpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.model';
import { UpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/customers/update-customer.model';
import { formatISO } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const CustomerUpdateFormStep2CommissionFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { customerCommissionPercentage } =
    useGatewayUpdateCustomerFormValidation();
  const { control } = useFormContext<
    Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    }
  >();

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        icon="currency-euro"
        text={t('delmonicos-shared-sdk.outputs-customer.commission-percentage')}
      />
      <Controller
        name="customer.commissionPercentage"
        rules={customerCommissionPercentage}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-customer.commission-percentage',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-customer.commission-percentage',
            )}
            type="number"
            required={!!customerCommissionPercentage.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? 0}
            onChange={(e) =>
              field.onChange(
                e.target.value === '' ? '' : e.target.valueAsNumber,
              )
            }
            adornment="%"
          />
        )}
      />
    </Card>
  );
};

const CustomerUpdateFormStep2BillingFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const {
    customerBillingDetailsFirstName,
    customerBillingDetailsLastName,
    customerBillingDetailsAddressAddress,
    customerBillingDetailsAddressCity,
    customerBillingDetailsAddressZipCode,
    customerBillingDetailsAddressCountryCode,
    customerIsExemptFromBilling,
  } = useGatewayUpdateCustomerFormValidation();
  const { control } = useFormContext<
    Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    }
  >();

  return (
    <Card flex={1} gap={GapEnum.M} direction="column">
      <AccentTitle
        icon="receipt-check"
        variant={AccentVariantEnum.Accent1}
        text={t('$.customer-update.step-2.billing.title')}
      />
      <Stack gap={GapEnum.M} justifyContent="space-between" flexWrap="wrap">
        <Controller
          name="customer.billingDetails.firstName"
          rules={customerBillingDetailsFirstName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t(
                'delmonicos-shared-sdk.outputs-billing-details.first-name',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-billing-details.first-name',
              )}
              required={!!customerBillingDetailsFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="customer.billingDetails.lastName"
          rules={customerBillingDetailsLastName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t(
                'delmonicos-shared-sdk.outputs-billing-details.last-name',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-billing-details.last-name',
              )}
              required={!!customerBillingDetailsLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Stack>

      <Controller
        name="customer.billingDetails.address.address"
        rules={customerBillingDetailsAddressAddress}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-address.address')}
            placeholder={t('delmonicos-shared-sdk.outputs-address.address')}
            required={!!customerBillingDetailsAddressAddress.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />

      <Stack gap={GapEnum.M} justifyContent="space-between" flexWrap="wrap">
        <Controller
          name="customer.billingDetails.address.zipCode"
          rules={customerBillingDetailsAddressZipCode}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              required={!!customerBillingDetailsAddressZipCode.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="customer.billingDetails.address.city"
          rules={customerBillingDetailsAddressCity}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Stack flex={1}>
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-address.city')}
                placeholder={t('delmonicos-shared-sdk.outputs-address.city')}
                required={!!customerBillingDetailsAddressCity.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            </Stack>
          )}
        />
        <Controller
          name="customer.billingDetails.address.countryCode"
          rules={customerBillingDetailsAddressCountryCode}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <CountryCode
              errorMessage={error?.message}
              fieldValue={field.value}
              fieldOnChange={field.onChange}
            />
          )}
        />
      </Stack>

      <Controller
        name="customer.isExemptFromBilling"
        rules={customerIsExemptFromBilling}
        control={control}
        render={({ field }) => (
          <Checkbox
            id="isExemptFromBilling"
            label={t(
              'delmonicos-shared-sdk.outputs-customer.is-exempt-from-billing',
            )}
            checked={field.value}
            onChange={(e) => field.onChange(e.target.checked)}
          />
        )}
      />
    </Card>
  );
};

const CustomerUpdateFormStep2BankFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const {
    customerBillingDetailsIban,
    customerBillingDetailsBic,
    customerBillingDetailsMandateDateOfSignature,
    customerBillingDetailsMandateDocumentNumber,
  } = useGatewayUpdateCustomerFormValidation();
  const { control, trigger, watch } = useFormContext<
    Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    }
  >();

  const sepaDateValue = watch(
    'customer.billingDetails.mandate.dateOfSignature',
  );
  const sepaDocumentNumberValue = watch(
    'customer.billingDetails.mandate.documentNumber',
  );

  return (
    <Card flex={1} gap={GapEnum.M} direction="column">
      <AccentTitle
        icon="bank"
        text={t('delmonicos-shared-sdk.outputs-customer.billing-details')}
        variant={AccentVariantEnum.Accent2}
      />

      <Controller
        name="customer.billingDetails.iban"
        rules={customerBillingDetailsIban}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.iban')}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-billing-details.iban',
            )}
            required={!!customerBillingDetailsIban.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />
      <Controller
        name="customer.billingDetails.bic"
        rules={customerBillingDetailsBic}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            placeholder={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            required={!!customerBillingDetailsBic.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />

      <Divider />

      <h2>{t('delmonicos-shared-sdk.outputs-billing-details.mandate')}</h2>

      <Controller
        name="customer.billingDetails.mandate.documentNumber"
        rules={customerBillingDetailsMandateDocumentNumber}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            required={!!sepaDateValue || !!sepaDocumentNumberValue}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
              trigger([
                'customer.billingDetails.mandate.documentNumber',
                'customer.billingDetails.mandate.dateOfSignature',
              ]);
            }}
          />
        )}
      />
      <Controller
        name="customer.billingDetails.mandate.dateOfSignature"
        rules={customerBillingDetailsMandateDateOfSignature}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            type="date"
            required={!!sepaDateValue || !!sepaDocumentNumberValue}
            errorMessage={error?.message}
            {...field}
            value={
              field.value ? formatISO(new Date(field.value)).split('T')[0] : ''
            }
            onChange={(e) => {
              field.onChange(new Date(e.target.value));
              trigger([
                'customer.billingDetails.mandate.documentNumber',
                'customer.billingDetails.mandate.dateOfSignature',
              ]);
            }}
          />
        )}
      />
    </Card>
  );
};

export const CustomerUpdateFormStep2Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} flex={1}>
      <Stack flex={1}>
        <CustomerUpdateFormStep2CommissionFragment />
      </Stack>
      <Stack flex={1}>
        <CustomerUpdateFormStep2BillingFragment />
      </Stack>
      <Stack flex={1}>
        <CustomerUpdateFormStep2BankFragment />
      </Stack>
    </Stack>
  );
};
